import { refresh } from "../utils/refresh";
import i18n from "i18next";
import Moment from "moment";
import FileSaver from "file-saver";
import employeeService from "../services/Employee.service";
import { history } from "../store";
import { apigClientFactory } from "../apiGateway/apigClientFactory";
import { addFlashMessageOfType } from 'redux-flash-messages'
import { ACTION_NAMES } from "../utils/actionConstants";
import { logAwardSummaryDownload } from "../utils/emfLogs";

const requestEmployee = (id) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_EMPLOYEE,
  payload: id,
});

const receiveEmployee = (employee) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_EMPLOYEE,
  payload: employee,
});

const unloadEmployee = () => ({
  type: ACTION_NAMES.EMPLOYEE.UNLOAD_EMPLOYEE,
  payload: null,
});

const requestChangeLanguage = (language) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_CHANGE_LANGUAGE,
  payload: language,
});

const receiveChangeLanguage = (language) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_LANGUAGE,
  payload: language,
});

const requestChangeTax = (awards, vests, tax_rate, apply_future = false) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_CHANGE_TAX,
  payload: {
    awards,
    vests,
    tax_rate,
    apply_future,
  },
});

const receiveChangeTax = (awards, vests, tax_rate, apply_future = false) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_TAX,
  payload: {
    awards,
    vests,
    tax_rate,
    apply_future,
  },
});

const receiveChangeTaxError = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_TAX_ERROR,
  payload: {
    error: true,
    errorMessage: i18n.t("ChangeTaxRateDialog.FailedToApply"),
  },
});

const requestChangeFractional = (employee, vests) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_CHANGE_FRACTIONAL,
  payload: {
    employee,
    vests,
  },
});

const receiveChangeFractional = (employee, vests) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_FRACTIONAL,
  payload: {
    employee,
    vests,
  },
});

const receiveChangeFractionalError = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_FRACTIONAL_ERROR,
  payload: {
    error: true,
    errorMessage: i18n.t("FractionalSharesDialog.FailedToApply"),
  },
});

const requestAcceptAward = (award_id) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_ACCEPT_AWARD,
  payload: award_id,
});

const receiveAcceptAward = (award_id) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_ACCEPT_AWARD,
  payload: award_id,
});

const receiveAcceptAwardError = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_ACCEPT_AWARD_ERROR,
  payload: {
    error: true,
    errorMessage: i18n.t("UnacceptedStockAwards.FailAcceptedAwardMessage"),
  },
});

const requestDownloadSummary = (
  employee_id,
  report_type,
  start_date,
  end_date
) => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_DOWNLOAD_SUMMARY,
  payload: {
    employee_id,
    report_type,
    start_date,
    end_date,
  },
});

const receiveDownloadSummary = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_DOWNLOAD_SUMMARY,
  payload: null,
});

const requestDocument = () => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_DOCUMENT,
  payload: null,
});

const receiveDocument = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_DOCUMENT,
  payload: null,
});

const requestChangeBroker = () => ({
  type: ACTION_NAMES.EMPLOYEE.REQUEST_CHANGE_BROKER,
  payload: null,
});

const receiveChangeBroker = (pending_broker) => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_BROKER,
  payload: pending_broker,
});

const receiveChangeBrokerError = () => ({
  type: ACTION_NAMES.EMPLOYEE.RECEIVE_CHANGE_BROKER,
  payload: {
    error: true,
    errorMessage: i18n.t("SelectBrokerDialog.FailedToChange"),
  },
});

export const actions = {
  unload: () => (dispatch, getState) => {
    dispatch(unloadEmployee());
  },
  load: () => async (dispatch, getState) => {
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    const id = state.auth.auth.id;
    const params = {
      id, // employee id,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };
    const body = {};
    const additionalParams = {};

    dispatch(requestEmployee(id));
    return apigClient
      .employeeIdGet(params, body, additionalParams)
      .then((result) => {
        console.log("success getting employee data");

        /* global digitalData */
        digitalData.selectedLanguage = result.data.language;
        digitalData.hrSourceCode = result.data.hr_source_code;
        digitalData.regRegion = result.data.region.code;

        // Let's load the app with the language set by the employee
        i18n.changeLanguage(result.data.language, (_) => {
          Moment.locale(result.data.language);
          dispatch(receiveEmployee(result.data));
        });
        return result.data;
      })
      .catch((error) => {
        console.log(error);
        console.log("Load Employee Data failed!!");
        //TODO handle error
      });
  },
  changeLanguage: (language) => async (dispatch, getState) => {
    console.log("Calling Select language in the server");
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestChangeLanguage(language));

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      language,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };

    var body = {};
    var additionalParams = {};

    return apigClient
      .changeLanguagePut(params, body, additionalParams)
      .then((result) => {
        console.log("success changing language");

        /* global digitalData */
        digitalData.buttonClicked = "Change Language";
        digitalData.selectedLanguage = language;
        document.body.dispatchEvent(new Event("button_clicked"));

        // Let's load the app with the language set by the employee
        dispatch(receiveChangeLanguage(language));
        i18n.changeLanguage(language, (_) => {
          Moment.locale(language);
        }).then(_ => {
          console.log("i18n: sending SUCCESS flash message " + i18n.t('ChangeLanguageDialog.LanguageChanged'))
          addFlashMessageOfType("SUCCESS", 5000, i18n.t('ChangeLanguageDialog.LanguageChanged'), null, null)
        });
      })
      .catch((error) => {
        console.log(error);
        console.log("Error. Setting language");
      });
  },
  changeLanguageV2: (language) => async (dispatch, getState) => {
    console.log("Calling Select language in the server");
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestChangeLanguage(language));

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      language,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };

    var body = {};
    var additionalParams = {};

    return apigClient
      .changeLanguagePut(params, body, additionalParams)
      .then((result) => {
        console.log("success changing language");

        /* global digitalData */
        digitalData.buttonClicked = "Change Language";
        digitalData.selectedLanguage = language;
        document.body.dispatchEvent(new Event("button_clicked"));

        // Let's load the app with the language set by the employee
        dispatch(receiveChangeLanguage(language));
        i18n.changeLanguage(language, (_) => {
          Moment.locale(language);
        }).then(_ => {
          console.log("i18n: sending SUCCESS flash message " + i18n.t('ChangeLanguageDialog.LanguageChanged'))
          addFlashMessageOfType("SUCCESS", false, i18n.t('ChangeLanguageDialog.LanguageChanged'))
        });
      })
      .catch((error) => {
        console.log(error);
        console.log("Error. Setting language");
      });
  },
  acceptAward: (id) => async (dispatch, getState) => {
    console.log("Calling Accept award");

    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestAcceptAward(id));
    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      award_id: id,
      accept_terms: true,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };
    var body = {};
    var additionalParams = {};

    /* global digitalData */
    digitalData.buttonClicked = "Accept Award";
    document.body.dispatchEvent(new Event("button_clicked"));

    return apigClient
      .acceptAwardPut(params, body, additionalParams)
      .then((result) => {
        console.log("success accept award");
        dispatch(receiveAcceptAward(id));
      })
      .catch((error) => {
        console.log(error);
        console.log("Error. Accepting award");
        dispatch(receiveAcceptAwardError());
        throw error;
      });
  },
  changeBroker: (broker_code, remove = false) => async (dispatch, getState) => {
    console.log("Calling change broker");

    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestChangeBroker());

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      broker_code: broker_code,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };

    var body = {};
    var additionalParams = {};

    if (remove) {
      return apigClient
        .removeBrokerDelete(params, body, additionalParams)
        .then((result) => {
          console.log("success remove broker", broker_code);
          // remove pending
          dispatch(receiveChangeBroker(null));

          /* global digitalData */
          digitalData.buttonClicked = "Cancel Change Broker";
          document.body.dispatchEvent(new Event("button_clicked"));
        })
        .catch((error) => {
          dispatch(receiveChangeBrokerError());
          console.log("Error. remove broker", error);
          throw error;
        });
    }

    return apigClient
      .changeBrokerPut(params, body, additionalParams)
      .then((result) => {
        console.log("success change broker", broker_code);
        // Load pending broker
        const pending_broker = employeeService.getBrokerByCode(
          state.configuration.brokers,
          broker_code
        );
        dispatch(receiveChangeBroker(pending_broker));

        /* global digitalData */
        digitalData.buttonClicked = "Change Broker";
        document.body.dispatchEvent(new Event("button_clicked"));
      })
      .catch((error) => {
        dispatch(receiveChangeBrokerError());
        console.log("Error. change broker", error);
        throw error;
      });
  },
  getSummaryDocument: (
    employee_id,
    report_type,
    start_date,
    end_date,
    download_type,
    award_id = null,
    start_calendar_month = null,
    end_calendar_month = null,
    year = null
  ) => async (dispatch, getState) => {
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    console.log("Report type: ", report_type);
    const parameters = {
      report_type: report_type,
      start_date: start_date,
      end_date: end_date,
      award_id: award_id,
      start_calendar_month: start_calendar_month,
      end_calendar_month: end_calendar_month,
      year: year,
    };

    const query_params = new URLSearchParams(parameters);
    const params_string = query_params.toString().replaceAll('null', '');

    const url = `${process.env.REACT_APP_API_GATEWAY}/employees/${employee_id}/award/summary/${download_type}?${params_string}`;

    /* global digitalData */
    digitalData.fileDownloadUrl = "Download Award Summary (UNKNOWN)";

    if (download_type === "pdf") {
      digitalData.fileDownloadUrl = "Download Award Summary (PDF)";
    } else if (download_type === "excel") {
      digitalData.fileDownloadUrl = "Download Award Summary (Excel)";
    }

    document.body.dispatchEvent(new Event("file_download"));

    const myRequest = new Request(url);

    dispatch(
      requestDownloadSummary(employee_id, report_type, start_date, end_date)
    );

    logAwardSummaryDownload(download_type);
    fetch(myRequest, {
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
        Accept: "application/pdf",
        Authorization: `Bearer ${state.auth.auth.secret}`,
      },
    }).then((response) => {
      response.blob().then((blob) => {
        const fileName = response.headers.get("X-filename");
        FileSaver.saveAs(blob, fileName);
        dispatch(receiveDownloadSummary());
      });
    });
  },
  getDocument: (employee_id, award_id, document_id) => async (
    dispatch,
    getState
  ) => {
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      // Reject rather than resolve: no document was downloaded, so callers that
      // gate on a successful download must not treat this as success.
      throw error;
    }

    dispatch(requestDocument());

    const myRequest = new Request(
      `${process.env.REACT_APP_API_GATEWAY}/employees/${employee_id}/award/${award_id}/document/${document_id}`
    );

    /* global digitalData */
    digitalData.fileDownloadUrl = "Download RSU Agreement";
    document.body.dispatchEvent(new Event("file_download"));

    // Return the chain so callers can await the actual download completing
    // (e.g. the accept-award dialogs gate acceptance on a successful download).
    return fetch(myRequest, {
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${state.auth.auth.secret}`,
      },
    }).then((response) => {
      // fetch only rejects on network errors, so reject non-2xx (e.g. 403/500)
      // explicitly — callers gate award acceptance on a successful download.
      if (!response.ok) {
        throw new Error(`Document download failed with status ${response.status}`);
      }
      return response.blob().then((blob) => {
        const fileName = response.headers.get("X-filename");
        FileSaver.saveAs(blob, fileName);
        dispatch(receiveDocument());
      });
    }).catch((error) => {
      // Clear the global pending-request flag so the app is not stuck behind the
      // loading dialog, then re-throw so callers can react to the failure.
      dispatch(receiveDocument());
      throw error;
    });
  },
  getDetailedTaxDocument: (
    report_type,
    employee_id,
    start_date,
    end_date,
    award_ids,
    transaction_ids
  ) => async (dispatch, getState) => {
    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    const parameters = {
      start_date,
      end_date,
      "award_ids[]": award_ids,
      report_type: report_type,
      "trans_ids[]": transaction_ids,
    };

    const query_params = new URLSearchParams(parameters);
    let params_string = query_params.toString().replaceAll('null', '');
    params_string = params_string.replaceAll('undefined', '');

    const url = `${process.env.REACT_APP_API_GATEWAY}/employees/${employee_id}/detailed_tax_report?${params_string}`;
    const myRequest = new Request(url);

    /* global digitalData */
    digitalData.fileDownloadUrl = "Download Tax Detail in (UNKNOWN)";

    if (report_type === "DETAIL_TAX_PDF") {
      digitalData.fileDownloadUrl = "Download Tax Detail in (PDF)";
    } else if (report_type === "DETAIL_TAX_EXCEL") {
      digitalData.fileDownloadUrl = "Download Tax Detail in (Excel)";
    } else if (report_type === "EMPLOYEE_EQUITY_PDF") {
      digitalData.fileDownloadUrl = "Download Equity Award Statement (PDF)";
    }

    dispatch(requestDocument());

    fetch(myRequest, {
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${state.auth.auth.secret}`,
      },
    }).then((response) => {
      document.body.dispatchEvent(new Event("file_download"));

      response.blob().then((blob) => {
        const fileName = response.headers.get("X-filename");
        FileSaver.saveAs(blob, fileName);
        dispatch(receiveDocument());
      });
    });
  },
  applyTaxRate: (awards, vests, tax_rate, apply_future = false) => async (
    dispatch,
    getState
  ) => {
    console.log("Calling change tax");

    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestChangeTax(awards, vests, tax_rate, apply_future));

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };
    var body = {
      all_future: apply_future,
      tax_rate: tax_rate,
      awards: awards,
      vests: vests,
    };
    var additionalParams = {};

    /* global digitalData */
    digitalData.buttonClicked = "Change Tax Rate";
    document.body.dispatchEvent(new Event("button_clicked"));

    return apigClient
      .changeTaxPut(params, body, additionalParams)
      .then((result) => {
        console.log("success changing tax rate");
        dispatch(receiveChangeTax(awards, vests, tax_rate, apply_future));
      })
      .catch((error) => {
        console.log(error);
        console.log("Error. Changing tax rate");
        dispatch(receiveChangeTaxError());
        throw error;
      });
  },
  applyFractionalIndicators: (employee, vests) => async (
    dispatch,
    getState
  ) => {
    console.log("Calling change fractional share indicators");

    const state = getState();

    try {
      await refresh(state.auth.auth);
    } catch (error) {
      console.log("Refresh Error: " + error);
      history.push("/logout");
      return;
    }

    dispatch(requestChangeFractional(employee, vests));

    var apigClient;
    if (process.env.REACT_APP_API_GATEWAY) {
      apigClient = apigClientFactory.newClient({
        invokeUrl: process.env.REACT_APP_API_GATEWAY,
      });
    } else {
      apigClient = apigClientFactory.newClient();
    }

    var params = {
      id: state.employee.id, // employee id,
      Authorization: `Bearer ${state.auth.auth.secret}`,
    };
    var body = {
      fractional_share_in: employee.fractional_share_in,
      vests: vests,
    };
    var additionalParams = {};

    /* global digitalData */
    digitalData.buttonClicked = "Change Fractional Share Indicators";
    document.body.dispatchEvent(new Event("button_clicked"));

    return apigClient
      .changeFractionalPut(params, body, additionalParams)
      .then((result) => {
        console.log("success changing fractional indicators");
        dispatch(receiveChangeFractional(employee, vests));
      })
      .catch((error) => {
        console.log(error);
        console.log("Error. Changing fractional indicators");
        dispatch(receiveChangeFractionalError());
        throw error;
      });
  },
};
